import React, { useState, useEffect } from "react";
import styles from "./attendance-details.module.css";
import { Checkbox, Select, Typography } from "antd";
import CustomTextfield from "../../../../../common/input/custom-textfield/custom-textfield";
import CustomSelect from "../../../../../common/input/custom-select/custom-select";
import CustomButton from "../../../../../common/custom-button/custom-button";
import CustomTable from "../../../../../common/custom-table/custom-table";
import SVGIcon from "../../../../../constants/SVGIcon";
import CustomDrawer from "../../../../../common/custom-drawer/custom-drawer";
import ViewDetails from "../../drawers-content/components/view-details/view-details";
import SetPreference from "../../drawers-content/set-preference/components/set-preference";
import {
  getLevelsById,
  getStudentAttendanceDetails,
  sendRemainder,
} from "../../../../../services/student";
import { fetchClass } from "../../../../../services/student";
import { toast } from "react-toastify";
import { setPreferenceData } from "../../../../../services/student";
import { Controller, useForm } from "react-hook-form";

const StudentAttendanceDetails = ({ selectedDate }) => {
  const [allForm, setAllForm] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [preferenceDrawer, SetPreferenceDrawer] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [filterPending, setFilterPending] = useState(false);
  const [filterIncomplete, setFilterIncomplete] = useState(false);
  const [formData, setFormData] = useState({});
  const [selectedClassroomIds, setSelectedClassroomIds] = useState([]);
  const [selectedFormsIds, setSelectedFormsIds] = useState([]);
  const [level, setLevels] = useState([]);
  const { control, trigger, getValues, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      level: null,
    },
  });

  const filteredData = allForm.filter((item) => {
    if (!filterPending && !filterIncomplete) return true;

    return item.classrooms.some(
      ({ status }) =>
        (filterPending && status === "PENDING") ||
        (filterIncomplete && status === "INCOMPLETE")
    );
  });

  const searchedData = filteredData.filter((item) => {
    const levelMatch = item?.level_name
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase());

    const classroomMatch = item?.classrooms?.some((cls) =>
      cls?.classroom_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return levelMatch || classroomMatch;
  });

  const handleSetPreference = async () => {
    try {
      setLoading(true);
      await setPreferenceData(formData);
      toast.success("Preference is set successfully");
      SetPreferenceDrawer(false);
    } catch (err) {
      console.error("Error setting preference", err);
      toast.error("Failed to set preference");
    } finally {
      setLoading(false);
    }
  };
  const fetchStudentAttendanceDetails = async () => {
    try {
      const params = { date: selectedDate };
      const response = await getStudentAttendanceDetails(params);
      setAllForm(response.data.data);
      return response.data.data;
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getLevels();
  }, []);

  useEffect(() => {
    fetchStudentAttendanceDetails();
  }, [selectedDate]);

  const getLevels = async () => {
    try {
      setLoading(true);
      const response = await fetchClass();
      if (response?.data) {
        const data = response?.data?.data?.levels;
        setLevels(data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const getLevelsby = async (id) => {
    try {
      setLoading(true);
      const response = await getLevelsById(id);
      if (response?.data) {
        const data = response?.data?.data;
        setAllForm(data);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleClassroomToggle = (classroomId, record) => {
    setSelectedClassroomIds((prev) => {
      if (prev.includes(classroomId)) {
        return prev.filter((id) => id !== classroomId);
      } else {
        return [...prev, classroomId];
      }
    });
  };

  const handleFormToggle = (level_id, record) => {
    setSelectedFormsIds((prev) => {
      const isSelected = prev.includes(level_id);

      const validClassroomIds = record.classrooms
        .filter((c) => c.status !== "COMPLETED")
        .map((c) => c.classroom_id);

      setSelectedClassroomIds((prevClassrooms) => {
        if (isSelected) {
          return prevClassrooms.filter((id) => !validClassroomIds.includes(id));
        } else {
          return Array.from(new Set([...prevClassrooms, ...validClassroomIds]));
        }
      });

      if (isSelected) {
        return prev.filter((id) => id !== level_id);
      } else {
        return [...prev, level_id];
      }
    });
  };

  const handleSendRemainder = async () => {
    if (selectedClassroomIds.length === 0) {
      toast.error("No classrooms selected");
      return;
    }

    const payload = { classroom_ids: selectedClassroomIds };

    try {
      setLoading(true);
      await sendRemainder(payload);

      toast.success(`Reminder sent for classrooms`);
      setSelectedClassroomIds([]);
      setSelectedFormsIds([]);
    } catch (err) {
      console.error("Error sending reminder", err);
    } finally {
      setLoading(false);
    }
  };

  const sumFun = (record, sumData) => {
    return record.classrooms.reduce(
      (acc, item) => (acc += item[sumData] || 0),
      0
    );
  };

  const columns = [
    {
      title: "Level Name",
      dataIndex: "level_name",
      key: "level_name",
      sorter: (a, b) => a.level_name.localeCompare(b.level_name),
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Checkbox
            disabled={record.status === "COMPLETED"}
            checked={
              record.status !== "COMPLETED" &&
              selectedFormsIds.includes(record.level_id)
            }
            onChange={() => handleFormToggle(record.level_id, record)}
          />
          {record.level_name}
        </div>
      ),
    },
    {
      title: "Classes",
      key: "classes",
      render: (_, record) =>
        record.classrooms.map((item) => item.classroom_name).join(", "),
      sorter: (a, b) =>
        a.classrooms
          .map((c) => c.classroom_name)
          .join(", ")
          .localeCompare(b.classrooms.map((c) => c.classroom_name).join(", ")),
    },
    {
      title: "Total Students",
      key: "total_students",
      render: (_, record) => {
        const total = sumFun(record, "total_students");
        return <span>{total}</span>;
      },
      sorter: (a, b) =>
        sumFun(a, "total_students") - sumFun(b, "total_students"),
    },
    {
      title: "Total Male Students",
      key: "maleStudents",
      render: (_, record) => {
        const total = sumFun(record, "male_count");
        const present = sumFun(record, "present_male_count");
        return <span>{`${present}/${total}`}</span>;
      },
      sorter: (a, b) =>
        sumFun(a, "present_male_count") - sumFun(b, "present_male_count"),
    },
    {
      title: "Total Female Students",
      key: "femaleStudents",
      render: (_, record) => {
        const total = sumFun(record, "female_count");
        const present = sumFun(record, "present_female_count");
        return <span>{`${present}/${total}`}</span>;
      },
      sorter: (a, b) =>
        sumFun(a, "present_female_count") - sumFun(b, "present_female_count"),
    },
    {
      title: "Attendance",
      key: "attendance",
      render: (_, record) => {
        const present = sumFun(record, "present_count");
        const total = sumFun(record, "total_students");
        return <span>{`${present}/${total}`}</span>;
      },
      sorter: (a, b) => sumFun(a, "present_count") - sumFun(b, "present_count"),
    },
    {
      title: "Status",
      key: "status",
      sorter: (a, b) => {
        const getStatus = (rec) =>
          rec.classrooms.some(
            (cls) => cls.status === "PENDING" || cls.status === "INCOMPLETE"
          )
            ? "ACTION REQUIRED"
            : "COMPLETED";

        return getStatus(a).localeCompare(getStatus(b));
      },
      render: (_, record) => {
        const hasPendingOrIncomplete = record.classrooms.some(
          (cls) => cls.status === "PENDING" || cls.status === "INCOMPLETE"
        );
        const text = hasPendingOrIncomplete ? "ACTION REQUIRED" : "COMPLETED";

        const colorMap = {
          COMPLETED: "#3BB44B",
          "ACTION REQUIRED": "#1E9EFD",
        };
        const bgColorMap = {
          COMPLETED: "#ECF8ED",
          "ACTION REQUIRED": "#F0F8FF",
        };

        return (
          <span
            className={styles.statusPill}
            style={{
              backgroundColor: bgColorMap[text],
              color: colorMap[text],
            }}
          >
            <span
              className={styles.statusDot}
              style={{ backgroundColor: colorMap[text] }}
            />
            <span>{text}</span>
          </span>
        );
      },
    },
  ];

  const handleViewDetails = (record) => {
    setSelectedRecord(record);
    setDrawerOpen(true);
  };

  const expandColumns = [
    {
      title: "Class",
      key: "classroom_name_child",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Checkbox
            disabled={record.status === "COMPLETED"}
            checked={
              record.status !== "COMPLETED" &&
              selectedClassroomIds.includes(record.classroom_id)
            }
            onChange={() => handleClassroomToggle(record.classroom_id, record)}
          />
          <span>{record.classroom_name}</span>
          {record.is_notified && (
            <SVGIcon
              name="notification-bell"
              width="24"
              height="24"
              className="anticon"
              stroke="#667085"
            />
          )}
        </div>
      ),
      sorter: (a, b) =>
        (a.classroom_name || "").localeCompare(b.classroom_name || ""),
    },

    {
      title: "Total Students",
      dataIndex: "total_students",
      key: "total_students",
      sorter: (a, b) => (a.total_students || 0) - (b.total_students || 0),
    },
    {
      title: "Male Students",
      key: "male_count_child",
      render: (_, record) => {
        const total_male = record.male_count || 0;
        const total_male_present = record.present_male_count || 0;
        return <span>{`${total_male_present}/${total_male}`}</span>;
      },
      sorter: (a, b) =>
        (a.present_male_count || 0) - (b.present_male_count || 0),
    },
    {
      title: "Female Students",
      key: "female_count_child",
      render: (_, record) => {
        const total_female = record.female_count || 0;
        const total_female_present = record.present_female_count || 0;
        return <span>{`${total_female_present}/${total_female}`}</span>;
      },
      sorter: (a, b) =>
        (a.present_female_count || 0) - (b.present_female_count || 0),
    },
    {
      title: "Attendance Allocation",
      key: "attendance_allocation_child",
      render: (_, record) => {
        const total_present = record.present_count || 0;
        const total_students = record.total_students || 0;
        return <span>{`${total_present}/${total_students}`}</span>;
      },
      sorter: (a, b) => (a.present_count || 0) - (b.present_count || 0),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status_child",
      sorter: (a, b) => (a.status || "").localeCompare(b.status || ""),
      render: (status) => {
        const colorMap = {
          COMPLETED: "#3BB44B",
          PENDING: "#d93025",
          INCOMPLETE: "#F0AF00",
        };

        const bgColorMap = {
          COMPLETED: "#ECF8ED",
          PENDING: "rgba(217, 48, 37, 0.1)",
          INCOMPLETE: "#FFFEE4",
        };

        return (
          <span
            className={styles.statusPill}
            style={{
              backgroundColor: bgColorMap[status],
              color: colorMap[status],
            }}
          >
            <span
              className={styles.statusDot}
              style={{ backgroundColor: colorMap[status] }}
            />
            <span>{status}</span>
          </span>
        );
      },
    },
    {
      title: "Action",
      key: "action_child",
      render: (_, record) => (
        <CustomButton
          title="View Details"
          size="small"
          onClick={() => handleViewDetails(record)}
        />
      ),
    },
  ];

  const expandedRowRender = (record) => (
    <CustomTable
      columns={expandColumns}
      dataSource={record?.classrooms || []}
      loading={loading}
      pagination={false}
    />
  );

  return (
    <>
      <div className={styles.main_container}>
        <div className={styles.title_container}>
          <Typography className={styles.title}>Attendance Details</Typography>
          <div className={styles.rightFieldContainer}>
            <div className={styles.filter_container}>
              <CustomTextfield
                id="search"
                type="text"
                className={styles.inputField}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                suffix={
                  <SVGIcon
                    name="search-icon"
                    width="24"
                    height="24"
                    className="anticon"
                    stroke="#667085"
                  />
                }
                placeholder="Search ..."
              />
            </div>
            <div className={styles.responsive_select}>
              <Controller
                name="level"
                control={control}
                rules={{ required: true }}
                render={({
                  field: { onChange, value },
                  fieldState: { error },
                }) => (
                  <CustomSelect
                    id="dateselect"
                    labelColor="#171717"
                    placeholder="--Select--"
                    size="large"
                    value={value}
                    onChange={(val) => {
                      onChange(val);

                      if (val === "all") {
                        fetchStudentAttendanceDetails();
                      } else {
                        getLevelsby(val);
                      }
                    }}
                  >
                    <Select.Option key="all" value="all">
                      All Form
                    </Select.Option>
                    {level?.map((option) => (
                      <Select.Option key={option.id} value={option.id}>
                        {option.name}
                      </Select.Option>
                    ))}
                  </CustomSelect>
                )}
              />
            </div>
            <CustomButton
              title="Set Preference"
              type="submit"
              style={{ height: "40px", marginRight: "7px", marginLeft: "12px" }}
              variant="outlined"
              icon={
                <SVGIcon
                  name="filter"
                  width="24"
                  height="24"
                  className="anticon"
                  stroke="#667085"
                />
              }
              onClick={() => SetPreferenceDrawer(true)}
            />
          </div>
        </div>
        <div className={styles.checkbox_container}>
          <Checkbox
            className={styles.checkbox_text}
            checked={filterPending}
            onChange={(e) => setFilterPending(e.target.checked)}
          >
            Select Pending Status
          </Checkbox>
          <Checkbox
            className={styles.checkbox_text}
            checked={filterIncomplete}
            onChange={(e) => setFilterIncomplete(e.target.checked)}
          >
            Select Incomplete Status
          </Checkbox>
        </div>
        <CustomTable
          columns={columns}
          dataSource={searchedData.map((item, idx) => ({
            ...item,
            key: item.level_id || idx,
          }))}
          collapsible={true}
          pagination={false}
          renderCollapseContent={expandedRowRender}
          handleExpand={(expand, record) => {
            if (expand) {
            }
          }}
          loading={loading}
        />

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "8px",
            padding: "7px 7px",
          }}
        >
          <CustomButton
            variant="secondary"
            title={<Typography>Cancel</Typography>}
            disabled={loading}
            style={{ backgroundColor: "white" }}
          />
          <CustomButton
            variant="filled"
            type="primary"
            title="Send Reminder"
            onClick={handleSendRemainder}
            disabled={loading || selectedClassroomIds.length === 0}
          />
        </div>
      </div>

      {drawerOpen && (
        <CustomDrawer
          title="Details"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width="50vw"
          loading={loading}
          className={styles.drawer}
          drawerStyles={{ body: { padding: 0, margin: 0 } }}
          destroyOnClose
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <CustomButton
                variant="secondary"
                title={<Typography>Cancel</Typography>}
                disabled={loading}
                style={{ backgroundColor: "white" }}
                onClick={() => setDrawerOpen(false)}
              />
            </div>
          }
        >
          <ViewDetails students={selectedRecord.students} />
        </CustomDrawer>
      )}

      {SetPreferenceDrawer && (
        <CustomDrawer
          title="Set Preference"
          open={preferenceDrawer}
          onClose={() => SetPreferenceDrawer(false)}
          width="50vw"
          loading={loading}
          className={styles.drawer}
          drawerStyles={{ body: { padding: 0, margin: 0 } }}
          destroyOnClose
          footer={
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "8px",
              }}
            >
              <CustomButton
                variant="secondary"
                title={<Typography>Cancel</Typography>}
                onClick={() => SetPreferenceDrawer(false)}
                disabled={loading}
                style={{ backgroundColor: "white" }}
              />
              <CustomButton
                variant="filled"
                type="primary"
                title="Save"
                onClick={handleSetPreference}
                loading={loading}
              />
            </div>
          }
        >
          <SetPreference
            loading={loading}
            setLoading={setLoading}
            onChange={(data) => setFormData(data)}
          />
        </CustomDrawer>
      )}
    </>
  );
};

export default StudentAttendanceDetails;
